/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback } from 'react';
import { IconWrapper, LabelWrapper, CheckboxWrapper, PillButton } from './filter-item.styled';
import { Checkbox } from '@atlaskit/checkbox';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';
import { token } from '@atlaskit/tokens';

export enum OptionVariant {
	CHECKBOX = 'CHECKBOX',
	PILL = 'PILL',
}

export interface Props {
	/**
	 * The icons of the filter.
	 */
	icon?: JSX.Element;
	/**
	 * The value of the filter that will be passed back through the `onChange` callback.
	 */
	value: string;
	/**
	 * The display text of the filter.
	 */
	label: string;
	/**
	 * Whether the filter is checked by default, this will only affect the state of the filter on mount.
	 * This will only be used if the component is used not controlled (see \`isChecked\`).
	 */
	defaultChecked?: boolean;
	/**
	 * Whether the filter is checked or not.
	 * If this is provided then the component is considered \`controlled\` and will not internally track the state of the filter.
	 */
	isChecked?: boolean;
	/**
	 * A callback that is called when the filter is selected / unselected. The value here is the same value as the \`value\` prop.
	 */
	onChange?: (value: string, isChecked: boolean) => void;
	/**
	 * A flag to determine if the checkbox is disabled
	 */
	isDisabled?: boolean;
	/**
	 * A react element to wrap the label text
	 */
	LabelComponent: React.ElementType;
	/**
	 * An optional prop for rendering option variants outside of the default checkbox
	 */
	variant?: string;
}

const FilterItem: React.FunctionComponent<Props> = ({
	value,
	onChange,
	label,
	isDisabled = false,
	icon,
	LabelComponent,
	variant,
	...rest
}) => {
	const onChangeCallback = useCallback(
		(e: any) => {
			if (variant === OptionVariant.PILL) {
				onChange?.(value, !rest.isChecked);
			} else {
				onChange?.(e.target.value, e.target.checked);
			}
		},
		[variant, onChange, value, rest.isChecked],
	);

	switch (variant) {
		case OptionVariant.PILL:
			return (
				<PillButton
					selected={rest.isChecked}
					aria-pressed={rest.isChecked}
					onClick={onChangeCallback}
				>
					{label}
				</PillButton>
			);
		case OptionVariant.CHECKBOX:
		default:
			return (
				<CheckboxWrapper>
					<Checkbox
						{...rest}
						value={value}
						onChange={onChangeCallback}
						// Target the svg to move to the left and down
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
						css={css({
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
							'& + svg': {
								margin: `${token('space.025')} 0 0 ${token('space.negative.050')}`,
							},
						})}
						isDisabled={isDisabled}
						label={
							<LabelWrapper>
								{icon && <IconWrapper>{icon}</IconWrapper>}
								<LabelComponent>{label}</LabelComponent>
							</LabelWrapper>
						}
					/>
				</CheckboxWrapper>
			);
	}
};

const _default_1: React.NamedExoticComponent<Props> = React.memo(FilterItem);
export default _default_1;
